import { useState } from "react";
import {
  Button,
  TextInput,
  Group,
  ActionIcon,
  PasswordInput,
} from "@mantine/core";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import { useForm, isEmail, isNotEmpty, matchesField } from "@mantine/form";
import { ReactComponent as GoogleIcon } from "@/assets/icons/google.svg";
import { ReactComponent as MailIcon } from "@/assets/icons/mail.svg";
import { ReactComponent as BackIcon } from "@/assets/icons/back.svg";
import { ReactComponent as Logo } from "@/assets/dinamo-logo.svg";
import { errorNotification } from "@/helpers/notifications";
import { useGoogleLogin } from "@react-oauth/google";
import { useMe } from "@/context/MeContext";
import { UserApi } from "@/api/user.api";
import { AuthApi } from "@/api/auth.api";
import "./Registration.css";

const Registration: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const { fetchMe } = useMe();

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      repeatPassword: "",
    },
    validate: {
      firstName: isNotEmpty("Ime je obavezno"),
      lastName: isNotEmpty("Prezime je obavezno"),
      email: isEmail("E-mail nije ispravan"),
      password: isNotEmpty("Lozinka je obavezna"),
      repeatPassword: matchesField("password", "Lozinke se ne podudaraju"),
    },
  });

  const registrationHandler = async (values: typeof form.values) => {
    const userData = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      password: values.password,
    };
    const result = await UserApi.createAccount(userData);
    if (!result.payload) {
      errorNotification({
        message: result.response.data.message,
      });
    } else await fetchMe();
  };

  const googleLogin = useGoogleLogin({
    flow: "implicit",
    scope: "openid email profile",
    onSuccess: async (response) => {
      try {
        await AuthApi.googleLogin(response.access_token);
        await fetchMe();
      } catch (error) {
        console.error("Google login failed", error);
        errorNotification({
          message: "Google prijava nije uspjela",
        });
      }
    },
    onError: (error) => {
      console.error("Google Login Error:", error);
      errorNotification({
        message: "Google prijava nije uspjela",
      });
    },
  });

  return (
    <div className="registration-container">
      <div
        className={clsx(
          "registration-card",
          step === 1 ? "gap-[4rem] !pt-[3rem]" : "gap-6 step-2"
        )}
      >
        {step === 1 ? (
          <>
            <div className="flex flex-col gap-[3rem]">
              <div className="registration-header">
                <div className="reg-mobile-logo">
                  <Logo className="w-20 h-20" />
                </div>
                <div className="text-center text-[#0b1a29] text-xl font-bold ">
                  Registracija
                </div>
              </div>

              <div className="flex flex-col gap-6">
                <Button
                  variant="outline"
                  color="#0b1a29"
                  radius="xl"
                  size="md"
                  className="!border-[#e6e6e6] !h-[3.25rem]"
                  leftSection={<GoogleIcon />}
                  onClick={() => googleLogin()}
                >
                  Prijava s Google računom
                </Button>
                <Button
                  variant="outline"
                  color="#0b1a29"
                  radius="xl"
                  size="md"
                  className="!border-[#e6e6e6] !h-[3.25rem]"
                  leftSection={<MailIcon />}
                  onClick={() => setStep(2)}
                >
                  Nastavi s e-mailom
                </Button>
              </div>
            </div>
            <div className="text-center mobile-bottom-padding">
              <span className="text-[#0b1a29] text-sm font-medium ">
                Već imaš račun?
              </span>
              <Button
                variant="transparent"
                color="#004a99"
                className="!px-1 hover:opacity-80"
                classNames={{
                  label: "text-[#004a99] text-sm font-bold underline",
                }}
                onClick={() => navigate("/login")}
              >
                Prijavi se
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-[2rem]">
              <Group
                className="cursor-pointer w-fit hover:opacity-80"
                onClick={() => setStep(1)}
              >
                <ActionIcon
                  variant="filled"
                  size="lg"
                  radius="xl"
                  className="!bg-[#f4f4f4]"
                  aria-label="Back"
                >
                  <BackIcon fill="#0B1A29" />
                </ActionIcon>
                <div className="text-[#0b1a29] text-base font-bold">Natrag</div>
              </Group>
              <div className="registration-mobile-title">Registracija</div>

              <form
                id="registration-form"
                className="flex flex-col gap-8 h-full justify-between mobile-bottom-padding"
                onSubmit={form.onSubmit(registrationHandler)}
              >
                <div className="flex flex-col gap-6">
                  <TextInput
                    label="Ime"
                    placeholder="Ime"
                    key={form.key("firstName")}
                    {...form.getInputProps("firstName")}
                  />
                  <TextInput
                    label="Prezime"
                    placeholder="Prezime"
                    key={form.key("lastName")}
                    {...form.getInputProps("lastName")}
                  />
                  <TextInput
                    label="E-mail"
                    placeholder="E-mail adresa"
                    key={form.key("email")}
                    {...form.getInputProps("email")}
                  />
                  <PasswordInput
                    label="Lozinka"
                    placeholder="Lozinka"
                    key={form.key("password")}
                    {...form.getInputProps("password")}
                  />
                  <PasswordInput
                    label="Potvrdi lozinku"
                    placeholder="Potvrdi lozinku"
                    key={form.key("repeatPassword")}
                    {...form.getInputProps("repeatPassword")}
                  />
                </div>
              </form>
            </div>
            <div className="create-button-container mobile-bottom-padding">
              <Button
                type="submit"
                form="registration-form"
                variant="filled"
                color="#004a99"
                radius="xl"
                size="md"
                className="flex-1 !h-[3.25rem]"
              >
                Kreiraj račun
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Registration;
